import React, { useState } from "react";
import { Button, Card, Checkbox, Descriptions, Divider, Input, Select, Space } from "antd";
import styled from "styled-components";
import { MAIN_BAND_ID } from "../../../constants";
import { AreaDto } from "../../areas/area.interface";
import { useResourceContext } from "../../common/resource/useResourceContext";
import { ScooterStatusType, TaskDefinitionDTO, VehicleListV2FilterValue, VehicleListV2QuickFilter } from "../service/scooter.interface";
import { ScootStatus } from "../service/scooter.utils";
import { toggleVehicleListV2QuickFilter } from "../service/vehicle-list-v2.utils";

interface Props {
  value: VehicleListV2FilterValue;
  qr: string;
  imei: string;
  showAdminFilters: boolean;
  isMainBand: boolean;
  currentBandId?: string;
  bands: any[];
  belongings: any[];
  areas: AreaDto[];
  taskDefinitions: TaskDefinitionDTO[];
  onChange: (value: VehicleListV2FilterValue) => void;
  onQrChange: (value: string) => void;
  onImeiChange: (value: string) => void;
  onReset: () => void;
  onSearchNow: () => void;
  mockVariant?: "CURRENT" | "A" | "B" | "C" | "D" | "E";
  areaLoading?: boolean;
  areaEnabled?: boolean;
  onAreaOpen?: () => void;
}

const MODELS = ["S5", "S7", "S9", "S11", "W1", "W7", "W9"];
const ALL_STATUSES = ScootStatus.map((status) => status.value) as ScooterStatusType[];

function booleanOptions(trueLabel = "O", falseLabel = "X") {
  return [
    { value: "true", label: trueLabel },
    { value: "false", label: falseLabel },
  ];
}

function optionalBoolean(value: boolean | undefined) {
  return value == null ? undefined : String(value);
}

function toOptionalBoolean(value?: string) {
  return value == null ? undefined : value === "true";
}

function numberValue(value?: number) {
  return value == null ? "" : String(value);
}

function toOptionalNumber(value: string) {
  if (value === "") return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function NumericFilterInput({ value, rawValue, onChange, placeholder }: {
  value?: number;
  rawValue?: string;
  onChange: (value?: number, rawValue?: string) => void;
  placeholder: string;
}) {
  return <Input allowClear value={rawValue ?? numberValue(value)} placeholder={placeholder} onChange={(event) => {
    const next = event.target.value;
    onChange(toOptionalNumber(next), next === "" ? undefined : next);
  }} />;
}

export function VehicleListV2Filter(props: Props) {
  const { value, qr, imei, showAdminFilters, isMainBand, bands, belongings, areas, taskDefinitions, onChange, onQrChange, onImeiChange, onReset, onSearchNow, mockVariant, areaLoading, areaEnabled, onAreaOpen } = props;
  const { R } = useResourceContext();
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [quickFilters, setQuickFilters] = useState({ collect: false, disuse: false });
  const showAdvanced = mockVariant !== "E" || advancedOpen;
  const patch = (next: Partial<VehicleListV2FilterValue>) => onChange({ ...value, quickFilter: undefined, ...next });
  const allStatusesSelected = ALL_STATUSES.every((status) => value.statuses?.includes(status));

  function toggleQuickFilter(next: VehicleListV2QuickFilter) {
    const result = toggleVehicleListV2QuickFilter(value, quickFilters, next, isMainBand, props.currentBandId);
    setQuickFilters(result.active);
    onChange(result.value);
  }

  return (
    <FilterCard title={R.text.e_scooters_e_bikes_filter}>
      <Descriptions
        bordered
        size="small"
        labelStyle={{ width: 112 }}
        column={{ xxl: 4, xl: 4, lg: 3, md: 2, sm: 1, xs: 1 }}
      >
        {showAdminFilters && <Descriptions.Item label={R.text.filter_and_action} span={4}>
          <Space wrap>
            <Button style={{ marginRight: 4 }} size="small" type={quickFilters.collect ? "primary" : "default"} onClick={() => toggleQuickFilter("COLLECT")}>
              {quickFilters.collect ? R.text.collector_filter_off : R.text.collector_filter_on}
            </Button>
            <Button style={{ marginRight: 4 }} size="small" type={quickFilters.disuse ? "primary" : "default"} onClick={() => toggleQuickFilter("DISUSE")}>
              {quickFilters.disuse ? R.text.disuse_filter_off : R.text.disuse_filter_on}
            </Button>
            <Button style={{ marginRight: 4 }} size="small" danger ghost type="primary" onClick={() => { setQuickFilters({ collect: false, disuse: false }); onReset(); }}>{R.text.initial}</Button>
          </Space>
        </Descriptions.Item>}
        {mockVariant === "E" && <Descriptions.Item label="상세 필터" span={4}>
          <Button size="small" onClick={() => {
            if (!advancedOpen) onAreaOpen?.();
            setAdvancedOpen(!advancedOpen);
          }}>
            {advancedOpen ? "상세 필터 접기" : "상세 필터 보기"}
          </Button>
        </Descriptions.Item>}

        <Descriptions.Item label={R.text.qr}>
          <Input
            allowClear
            value={qr}
            onChange={(event) => onQrChange(event.target.value)}
            onPressEnter={onSearchNow}
            placeholder={R.text.qr}
          />
        </Descriptions.Item>
        <Descriptions.Item label={R.text.imei}>
          <Input
            allowClear
            value={imei}
            onChange={(event) => onImeiChange(event.target.value)}
            onPressEnter={onSearchNow}
            placeholder={R.text.imei}
          />
        </Descriptions.Item>
        {showAdminFilters && <Descriptions.Item label={R.text.device_model}>
          <Select showSearch optionFilterProp="label" mode="multiple" allowClear value={value.models} onChange={(models) => patch({ models })} placeholder="S9/S11..." options={MODELS.map((model) => ({ value: model, label: model }))} />
        </Descriptions.Item>}
        {showAdminFilters && (
          <Descriptions.Item label={R.text.region_name}>
            <Select showSearch optionFilterProp="label"
              mode="multiple"
              allowClear
              value={value.bandIds}
              onChange={(bandIds) => patch({ bandIds, allBands: bandIds.length === 0 })}
              placeholder={R.text.region_name}
              options={bands
                .slice()
                .sort((a, b) => a.id === MAIN_BAND_ID ? -1 : b.id === MAIN_BAND_ID ? 1 : 0)
                .map((band) => ({ value: band.id, label: band.name }))}
            />
          </Descriptions.Item>
        )}
        {showAdminFilters && (
          <Descriptions.Item label={R.text.device_owner}>
            <Select showSearch optionFilterProp="label" mode="multiple" allowClear value={value.belongingIdsRaw ?? value.belongingIds} onChange={(belongingIdsRaw) => patch({ belongingIds: undefined, belongingIdsRaw })} placeholder={R.text.headquarters} options={belongings.map((item) => ({ value: item.id, label: item.description }))} />
          </Descriptions.Item>
        )}
        <Descriptions.Item label={R.text.battery}>
          <NumericFilterInput value={value.battery} rawValue={value.batteryRaw} onChange={(battery, batteryRaw) => patch({ battery, batteryRaw })} placeholder="--%" />
        </Descriptions.Item>
        {showAdvanced && <Descriptions.Item label={R.text.gps_location}>
          <div style={{ display: "flex", gap: 6, width: "100%" }}>
            <Select showSearch optionFilterProp="label"
              mode="multiple" allowClear value={value.areaIds}
              disabled={mockVariant === "B" && !areaEnabled}
              loading={areaLoading}
              onDropdownVisibleChange={(open) => { if (open) onAreaOpen?.(); }}
              onChange={(areaIds) => patch({ areaIds })}
              placeholder={mockVariant === "B" && !areaEnabled ? "사용을 눌러주세요" : `${R.text.seoul} ~~`}
              notFoundContent={areaLoading ? "지역 불러오는 중..." : undefined}
              options={areas.map((area) => ({ value: area.id, label: area.name }))}
            />
            {mockVariant === "B" && <Button size="small" loading={areaLoading} disabled={areaEnabled} onClick={onAreaOpen}>
              {areaEnabled ? "사용 중" : "사용"}
            </Button>}
          </div>
        </Descriptions.Item>}
        <Descriptions.Item label={R.text.ride_status}>
          <Select showSearch optionFilterProp="label" allowClear value={optionalBoolean(value.riding)} onChange={(next) => patch({ riding: toOptionalBoolean(next) })} placeholder="O/X" options={booleanOptions()} />
        </Descriptions.Item>
        <Descriptions.Item label={R.text.lock_status}>
          <Select showSearch optionFilterProp="label" allowClear value={optionalBoolean(value.locked)} onChange={(next) => patch({ locked: toOptionalBoolean(next) })} placeholder={`${R.text.locked}/${R.text.unlock}`} options={booleanOptions(R.text.locked, R.text.unlock)} />
        </Descriptions.Item>
        <Descriptions.Item label={R.text.iot_status}>
          <Select showSearch optionFilterProp="label" allowClear value={optionalBoolean(value.online)} onChange={(next) => patch({ online: toOptionalBoolean(next) })} placeholder={`${R.text.offline}/${R.text.normal}`} options={[{ value: "false", label: R.text.offline }, { value: "true", label: R.text.normal }]} />
        </Descriptions.Item>
        <Descriptions.Item label={R.text.device_status}>
          <Select showSearch optionFilterProp="label" mode="multiple" allowClear value={value.statuses} onChange={(statuses) => patch({ statuses })} placeholder={`${R.text.device_status}..`} options={ScootStatus.map((status) => ({ value: status.value, label: status.text }))}
            dropdownRender={(menu) => <>
              <div style={{ padding: "4px 12px" }} onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}>
                <Checkbox
                  checked={allStatusesSelected}
                  indeterminate={!!value.statuses?.length && !allStatusesSelected}
                  onChange={(event) => patch({ statuses: event.target.checked ? ALL_STATUSES : [] })}
                >
                  전체 선택
                </Checkbox>
              </div>
              <Divider style={{ margin: "4px 0" }} />
              {menu}
            </>}
          />
        </Descriptions.Item>
        <Descriptions.Item label="고장 여부">
          <Select showSearch optionFilterProp="label" allowClear value={optionalBoolean(value.broken)} onChange={(next) => patch({ broken: toOptionalBoolean(next) })} placeholder="O/X" options={booleanOptions()} />
        </Descriptions.Item>
        {showAdvanced && <Descriptions.Item label="백업모드 여부">
          <Select showSearch optionFilterProp="label" allowClear value={optionalBoolean(value.backupMode)} onChange={(next) => patch({ backupMode: toOptionalBoolean(next) })} placeholder="O/X" options={booleanOptions()} />
        </Descriptions.Item>}
        <Descriptions.Item label="이용가능여부">
          <Select showSearch optionFilterProp="label" allowClear value={optionalBoolean(value.available)} onChange={(next) => patch({ available: toOptionalBoolean(next) })} placeholder="O/X" options={booleanOptions()} />
        </Descriptions.Item>
        <Descriptions.Item label={R.text.placement_date}>
          <NumericFilterInput value={value.allocatedDays} rawValue={value.allocatedDaysRaw} onChange={(allocatedDays, allocatedDaysRaw) => patch({ allocatedDays, allocatedDaysRaw })} placeholder={R.text.placement_date} />
        </Descriptions.Item>
        <Descriptions.Item label={R.text.inspection_status}>
          <Select showSearch optionFilterProp="label" allowClear value={optionalBoolean(value.inspected)} onChange={(next) => patch({ inspected: toOptionalBoolean(next) })} placeholder="O/X" options={booleanOptions()} />
        </Descriptions.Item>
        {showAdvanced && <Descriptions.Item label={R.text.last_iot_received}>
          <NumericFilterInput value={value.dataUpdatedDays} rawValue={value.dataUpdatedDaysRaw} onChange={(dataUpdatedDays, dataUpdatedDaysRaw) => patch({ dataUpdatedDays, dataUpdatedDaysRaw })} placeholder={R.text.last_iot_received} />
        </Descriptions.Item>}
        {showAdvanced && <Descriptions.Item label="마지막 작업일자">
          <NumericFilterInput value={value.lastWorkDays} rawValue={value.lastWorkDaysRaw} onChange={(lastWorkDays, lastWorkDaysRaw) => patch({ lastWorkDays, lastWorkDaysRaw })} placeholder="마지막 작업일자" />
        </Descriptions.Item>}
        {showAdvanced && <Descriptions.Item label={R.text.last_ride}>
          <NumericFilterInput value={value.lastRideDays} rawValue={value.lastRideDaysRaw} onChange={(lastRideDays, lastRideDaysRaw) => patch({ lastRideDays, lastRideDaysRaw })} placeholder={R.text.last_ride} />
        </Descriptions.Item>}
        {isMainBand && showAdvanced && (
          <Descriptions.Item label="재팬기기">
            <Select showSearch optionFilterProp="label" allowClear value={optionalBoolean(value.fromJapan)} onChange={(next) => patch({ fromJapan: toOptionalBoolean(next) })} placeholder="O/X" options={booleanOptions()} />
          </Descriptions.Item>
        )}
        <Descriptions.Item label="작업상태">
          <Select showSearch optionFilterProp="label"
            mode="multiple"
            allowClear
            value={value.workDefinitionNames}
            onChange={(workDefinitionNames) => patch({ workDefinitionNames })}
            placeholder="배터리교체하기..."
            options={[...taskDefinitions.map((item) => ({ value: item.name, label: item.displayName })), { value: "NOTHING", label: "-" }]}
          />
        </Descriptions.Item>
      </Descriptions>
    </FilterCard>
  );
}

const FilterCard = styled(Card)`
  .ant-select,
  .ant-input-affix-wrapper,
  .ant-input {
    width: 100%;
  }
`;
